import dynamic from "next/dynamic";
import React from "react";
import type { SanityRouteDocument, SanitySiteSettingsNavigation } from "@ouster/shared";
import type { SanityPageBuilderRenderSection } from "~/features/sanity/page-builder/renderer";
import { hasItems } from "~/features/app/utils";
import { up } from "~/features/app/screens";
import { useMediaQuery } from "~/features/ui/use-media-query";
import { NavAdditionalLinks } from "~/features/app/app-header-v2/nav-additional-links";
import { SubNavLinks } from "~/features/sub-nav/sub-nav-links";
import { SubNavAnchors } from "~/features/sub-nav/sub-nav-anchors";
import { SubNavLayoutProvider, type SubNavMode } from "~/features/sub-nav/context";
import { useSectionScroll } from "~/features/sub-nav/use-section-scroll";
import { adaptSanitySiteSettingsToAppLayoutCommon, adaptSanitySubNavAnchors, adaptSanitySubNavLinks, type CraftSubNavLink } from "~/features/sanity/layout/adapter";

// `SubNav` reads `window.innerHeight`, so it must not server-render (same as the
// removed dedicated hardware/industry routes).
const SubNav = dynamic(() => import("~/features/sub-nav").then(mod => mod.SubNav), {
  ssr: false
});
type SubNavRoute = Pick<SanityRouteDocument, "subNavLinks" | "subNavMode" | "title">;
type Props = {
  route: SubNavRoute;
  sections: SanityPageBuilderRenderSection[];
  siteSettings: SanitySiteSettingsNavigation | null;
};

/**
 * Renders the route-local sub-nav (product/industry style) on the Sanity
 * catch-all for documents that author `subNavLinks`. Mirrors the behavior the
 * removed dedicated routes had: a "current page" dropdown of `subNavLinks`,
 * in-page anchors derived from the loaded page-builder sections, and the global
 * additional nav links on desktop. The guard wrapper keeps the scroll/media
 * hooks out of the render path for pages without a sub-nav.
 */
export function SanityRouteSubNav({
  route,
  sections,
  siteSettings
}: Props) {
  const subNavLinks = adaptSanitySubNavLinks(route.subNavLinks);
  if (!hasItems(subNavLinks)) {
    return null;
  }
  return <SanityRouteSubNavInner route={route} sections={sections} siteSettings={siteSettings} subNavLinks={subNavLinks} />;
}
function SanityRouteSubNavInner({
  route,
  sections,
  siteSettings,
  subNavLinks
}: Props & {
  subNavLinks: CraftSubNavLink[];
}) {
  const subNavMode: SubNavMode = route.subNavMode || "light";
  const anchors = adaptSanitySubNavAnchors(sections);
  const navAdditionalLinks = adaptSanitySiteSettingsToAppLayoutCommon(siteSettings).navAdditionalLinks;
  const activeSectionId = useSectionScroll();
  const isLg = useMediaQuery(up("lg"));
  return <SubNavLayoutProvider mode={subNavMode}>
      <SubNav anchors={<SubNavAnchors anchors={anchors} activeSectionId={activeSectionId} />} navLinks={<SubNavLinks currentPageTitle={route.title || ""} navLinks={subNavLinks as unknown as React.ComponentProps<typeof SubNavLinks>["navLinks"]} />} navAdditionalLinks={isLg ? <NavAdditionalLinks variant={subNavMode === "light" ? "solid" : "transparent"} navLinks={navAdditionalLinks as unknown as React.ComponentProps<typeof NavAdditionalLinks>["navLinks"]} /> : null} />
    </SubNavLayoutProvider>;
}